import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Badge
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import {
  CustomPagination,
  TableToggle,
  ExamTypeChip,
  CustomTableContainer,
  CustomTable,
  CustomTableHead,
  CustomTableRow,
  CustomTableCell,
  CustomTableBody
} from '../../common';
import examService from '../../../infrastructure/services/examService';
import { formatSriLankanDate, parseSriLankanDateTime } from '../../../infrastructure/services/timeSyncService';

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

const makeMockResultsForScore = (scorePercent) => {
  return {
    answers: {},
    questions: [],
    totalCount: 0,
    correctCount: 0,
    wrongCount: 0,
    skippedCount: 0,
    scorePercentage: scorePercent
  };
};

export default function GradedExamsTab({ subjectFilter, teacherName, onStatsFetched, refreshTrigger }) {
  const navigate = useNavigate();
  const { teacherId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filter States
  const [historyStatus, setHistoryStatus] = useState('graded'); // 'graded' or 'missed'
  const [showFilters, setShowFilters] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedExamType, setSelectedExamType] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);

  useEffect(() => {
    const fetchExams = async () => {
      setLoading(true);
      try {
        const data = await examService.getAssignedExams({
          teacherId,
          classId: selectedClassId || undefined,
          status: historyStatus,
          examType: selectedExamType || undefined,
          sortBy: 'newest',
          page: currentPage,
          pageSize: 10
        });
        if (data && data.isSuccessful) {
          setExams(data.listContent || []);
          setTotalPages(data.paginationInfo?.totalPages || 1);
          setTotalItems(data.paginationInfo?.totalItems || 0);

          // Update available classes from the list
          if (data.listContent) {
            setAvailableClasses((prev) => {
              const seen = new Map(prev.map(c => [c.classId, c.className]));
              data.listContent.forEach(item => {
                if (item.classId && item.className) {
                  seen.set(item.classId, item.className);
                }
              });
              return Array.from(seen.entries()).map(([classId, className]) => ({ classId, className }));
            });
          }
          if (data.stats && onStatsFetched) {
            const statsMap = {};
            data.stats.forEach(item => {
              statsMap[item.id] = item.count;
            });
            onStatsFetched({
              total: statsMap.total ?? 0,
              completed: statsMap.completed ?? 0,
              ongoing: statsMap.ongoing ?? 0,
              upcoming: statsMap.upcoming ?? 0,
              missed: statsMap.missed ?? 0
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch graded exams:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, [teacherId, selectedClassId, selectedExamType, currentPage, onStatsFetched, historyStatus, refreshTrigger]);

  const filteredExams = exams.filter((exam) => {
    const title = stripHtml(exam.examTitle).toLowerCase();
    const search = searchQuery.toLowerCase();
    return (
      title.includes(search) ||
      (exam.classExamId && exam.classExamId.toLowerCase().includes(search)) ||
      (exam.subjectName && exam.subjectName.toLowerCase().includes(search)) ||
      (exam.className && exam.className.toLowerCase().includes(search))
    );
  });

  // Shared chip styling handled by common component ExamTypeChip

  const getGradeChip = (grade) => {
    let color = '#64748b'; // Default grey
    let bgcolor = '#f1f5f9';
    const normalized = (grade || '').toUpperCase();
    
    if (normalized.startsWith('A')) {
      color = '#10b981'; // Green
      bgcolor = '#ecfdf5';
    } else if (normalized.startsWith('B')) {
      color = '#f59e0b'; // Amber/Yellow
      bgcolor = '#fffbeb';
    } else if (normalized.startsWith('C')) {
      color = '#3b82f6'; // Blue
      bgcolor = '#eff6ff';
    } else if (normalized.startsWith('S')) {
      color = '#8b5cf6'; // Purple
      bgcolor = '#f3e8ff';
    } else if (normalized.startsWith('F')) {
      color = '#ef4444'; // Red
      bgcolor = '#fef2f2';
    }
    
    return (
      <Box
        sx={{
          bgcolor: bgcolor,
          color: color,
          fontWeight: 800,
          fontFamily: 'Poppins',
          borderRadius: '8px',
          fontSize: '13.5px',
          height: '28px',
          width: '28px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1.5px solid ${color}20`
        }}
      >
        {grade}
      </Box>
    );
  };

  const getGradeFromScore = (scorePercent) => {
    if (scorePercent >= 75) return 'A';
    if (scorePercent >= 65) return 'B';
    if (scorePercent >= 50) return 'C';
    if (scorePercent >= 35) return 'S';
    return 'F';
  };

  const safeCurrentPage = Math.min(currentPage, totalPages);

  return (
    <Box sx={{ py: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: '1.5px solid #e2e8f0',
          borderRadius: '24px',
          bgcolor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        }}
      >
        {/* Header and Actions Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3
          }}
        >
          {/* Title Block */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#262626', fontFamily: 'Poppins' }}>
                History
              </Typography>
              <Chip
                label={`${totalItems} Exams`}
                size="small"
                sx={{
                  bgcolor: '#ECFDF5',
                  color: '#10B981',
                  fontWeight: 500,
                  fontSize: '12px',
                  fontFamily: 'Poppins',
                  borderRadius: '16px',
                  height: '22px'
                }}
              />
            </Box>
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '13px',
                fontFamily: 'Poppins',
                mt: 0.5
              }}
            >
              Completed and missed exams from your classes
            </Typography>
          </Box>

          {/* Search Input and Filter Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <TableToggle
              options={[
                { id: 'graded', label: 'Graded' },
                { id: 'missed', label: 'Missed' }
              ]}
              value={historyStatus}
              onChange={(val) => {
                setHistoryStatus(val);
                setCurrentPage(1);
              }}
              layoutId="gradedExamsActiveTabBackground"
            />
            <TextField
              placeholder="Search"
              size="small"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              sx={{
                width: { xs: '100%', sm: 200 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#64748b' }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                  </InputAdornment>
                )
              }}
            />
            
            {(() => {
              const hasActiveFilters = !!selectedClassId || !!selectedExamType;
              return (
                <Button
                  variant="outlined"
                  onClick={() => setShowFilters(!showFilters)}
                  startIcon={
                    <Badge
                      variant="dot"
                      invisible={!hasActiveFilters}
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#FF502D',
                          boxShadow: '0 0 0 2px #fff',
                        }
                      }}
                    >
                      <FilterListRoundedIcon />
                    </Badge>
                  }
                  sx={{
                    borderColor: (showFilters || hasActiveFilters) ? '#FF502D' : '#cbd5e1',
                    color: (showFilters || hasActiveFilters) ? '#FF502D' : '#64748b',
                    bgcolor: hasActiveFilters ? 'rgba(255, 80, 45, 0.03)' : 'transparent',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontFamily: 'Poppins',
                    fontSize: '13px',
                    height: '40px',
                    '&:hover': {
                      borderColor: '#FF502D',
                      color: '#FF502D',
                      bgcolor: 'rgba(255, 80, 45, 0.08)'
                    }
                  }}
                >
                  Filters
                </Button>
              );
            })()}
          </Box>
        </Box>

        {/* Collapsible Filter Panel */}
        {showFilters && (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              mb: 3,
              p: 2,
              borderRadius: '16px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0'
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="class-filter-label" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Class</InputLabel>
              <Select
                labelId="class-filter-label"
                value={selectedClassId}
                label="Class"
                onChange={(e) => {
                  setSelectedClassId(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{
                  borderRadius: '10px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#cbd5e1' }
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>All Classes</MenuItem>
                {availableClasses.map((cls) => (
                  <MenuItem key={cls.classId} value={cls.classId} sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>
                    {cls.className}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="type-filter-label" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Exam Type</InputLabel>
              <Select
                labelId="type-filter-label"
                value={selectedExamType}
                label="Exam Type"
                onChange={(e) => {
                  setSelectedExamType(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{
                  borderRadius: '10px',
                  fontFamily: 'Poppins',
                  fontSize: '13px',
                  bgcolor: '#ffffff',
                  '& fieldset': { borderColor: '#cbd5e1' }
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>All Types</MenuItem>
                <MenuItem value="mcq" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>MCQ</MenuItem>
                <MenuItem value="essay" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Essay</MenuItem>
                <MenuItem value="structured" sx={{ fontFamily: 'Poppins', fontSize: '13px' }}>Structure</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="text"
              onClick={() => {
                setSelectedClassId('');
                setSelectedExamType('');
                setCurrentPage(1);
              }}
              sx={{
                color: '#ef4444',
                fontWeight: 600,
                fontFamily: 'Poppins',
                fontSize: '13px',
                textTransform: 'none',
                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.05)' }
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}

        {/* Table Area */}
        <CustomTableContainer sx={{ border: 'none', overflowX: 'auto' }}>
          <CustomTable size="medium">
            <CustomTableHead>
              <CustomTableRow>
                {(historyStatus === 'missed'
                  ? [
                      { label: 'Exam Title' },
                      { label: 'Class' },
                      { label: 'Subject' },
                      { label: 'Scheduled Date & Time' },
                      { label: 'Type' },
                      { label: 'Status' }
                    ]
                  : [
                      { label: 'Exam Title' },
                      { label: 'Class and Subject' },
                      { label: 'Duration Taken' },
                      { label: 'Completed Date' },
                      { label: 'Marks' },
                      { label: 'Grade' },
                      { label: 'Type' }
                    ]
                ).map((col) => (
                  <CustomTableCell
                    key={col.label}
                    sx={{
                      color: '#94a3b8',
                      fontWeight: 600,
                      fontSize: '12px',
                      fontFamily: 'Poppins',
                      borderBottom: '1px solid #f1f5f9',
                      py: 2,
                      bgcolor: '#ffffff'
                    }}
                  >
                    {col.label}
                  </CustomTableCell>
                ))}
                {/* Actions column */}
                <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2, bgcolor: '#ffffff' }} />
              </CustomTableRow>
            </CustomTableHead>
            <CustomTableBody>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <CustomTableRow key={index}>
                    {historyStatus === 'missed' ? (
                      <>
                        {/* Exam Title */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="70%" height={24} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Class */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="50%" height={20} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Subject */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Scheduled Date & Time */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="40%" height={20} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Type */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '8px' }} />
                        </CustomTableCell>
                        {/* Status */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="rectangular" width={50} height={22} sx={{ borderRadius: '6px' }} />
                        </CustomTableCell>
                      </>
                    ) : (
                      <>
                        {/* Exam Title and ID */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="70%" height={24} sx={{ borderRadius: '4px', mb: 0.5 }} />
                          <Skeleton variant="text" width="40%" height={16} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Subject and Class */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="60%" height={20} sx={{ borderRadius: '4px', mb: 0.5 }} />
                          <Skeleton variant="text" width="30%" height={16} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Duration Taken */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="40%" height={20} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Completed Date */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="50%" height={20} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Marks */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="text" width="30%" height={20} sx={{ borderRadius: '4px' }} />
                        </CustomTableCell>
                        {/* Grade */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="rectangular" width={40} height={22} sx={{ borderRadius: '6px' }} />
                        </CustomTableCell>
                        {/* Type */}
                        <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                          <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '8px' }} />
                        </CustomTableCell>
                      </>
                    )}
                    {/* Actions */}
                    <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                      <Skeleton variant="circular" width={32} height={32} sx={{ display: 'inline-block' }} />
                    </CustomTableCell>
                  </CustomTableRow>
                ))
              ) : filteredExams.length === 0 ? (
                <CustomTableRow>
                  <CustomTableCell colSpan={historyStatus === 'missed' ? 7 : 8} align="center" sx={{ py: 8, color: '#94a3b8', fontFamily: 'Poppins' }}>
                    {historyStatus === 'missed' ? 'No missed exams found.' : 'No graded exams found.'}
                  </CustomTableCell>
                </CustomTableRow>
              ) : (
                filteredExams.map((exam) => {
                  const isExamGrading = exam.isGrading || (exam.examType !== 'mcq' && exam.gradingStatus !== 'completed');
                  const scorePercent = exam.percentage !== null ? exam.percentage : (exam.totalScore || 0);
                  const duration = `${exam.durationHours}H ${exam.durationMinutes}M`;
                  const gradedDate = exam.gradedAt ? formatSriLankanDate(exam.gradedAt) : 'N/A';
                  const grade = getGradeFromScore(scorePercent);
                  const scheduledDateObj = parseSriLankanDateTime(exam.scheduledDate, exam.scheduledTime || '00:00');
                  const scheduledDateTime = scheduledDateObj ? `${formatSriLankanDate(scheduledDateObj)} ${exam.scheduledTime || ''}`.trim() : 'N/A';

                  const handleRowClick = () => {
                    if (isExamGrading) return;
                    if (historyStatus === 'missed') return;
                    const results = makeMockResultsForScore(scorePercent);
                    const mappedExam = {
                      id: exam.classExamId,
                      name: stripHtml(exam.examTitle),
                      subject: exam.subjectName,
                      topic: exam.className,
                      duration: duration,
                      type: exam.examType === 'mcq' ? 'MCQ' : exam.examType === 'essay' ? 'Essay' : 'Structure',
                      teacher: teacherName || 'Teacher',
                      teacherId,
                      date: gradedDate,
                      paperUploadId: exam.paperUploadId
                    };
                    navigate(`/exam-manager/exam-summary/${exam.classExamId}`, {
                      state: {
                        exam: mappedExam,
                        isCompleted: true,
                        results,
                        fromRoute: historyStatus === 'graded'
                          ? `/exam-manager/exams/${teacherId}/history`
                          : `/exam-manager/exams/${teacherId}`
                      }
                    });
                  };

                  return (
                    <CustomTableRow
                      key={exam.classExamId}
                      hover={!isExamGrading && historyStatus !== 'missed'}
                      onClick={handleRowClick}
                      sx={{
                        '&:last-child td': { borderBottom: 'none' },
                        cursor: (historyStatus === 'missed' || isExamGrading) ? 'default' : 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          bgcolor: (historyStatus === 'missed' || isExamGrading) ? 'transparent' : '#fffbf9 !important',
                          '& td': { borderColor: (historyStatus === 'missed' || isExamGrading) ? '#f1f5f9' : '#ffece8' }
                        }
                      }}
                    >
                      {historyStatus === 'missed' ? (
                        <>
                          {/* Exam Name */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', fontFamily: 'Poppins' }}>
                                {stripHtml(exam.examTitle)}
                              </Typography>
                            </Box>
                          </CustomTableCell>

                          {/* Class */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', fontFamily: 'Poppins' }}>
                              {exam.className}
                            </Typography>
                          </CustomTableCell>

                          {/* Subject */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins' }}>
                              {exam.subjectName}
                            </Typography>
                          </CustomTableCell>

                          {/* Scheduled Date & Time */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                            {scheduledDateTime}
                          </CustomTableCell>

                          {/* Type */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                            <ExamTypeChip type={exam.examType} />
                          </CustomTableCell>

                          {/* Status */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                            <Chip
                              label="Missed"
                              size="small"
                              sx={{
                                bgcolor: '#FEF2F2',
                                color: '#EF4444',
                                fontWeight: 700,
                                fontFamily: 'Poppins',
                                borderRadius: '6px',
                                fontSize: '11px',
                                height: '22px'
                              }}
                            />
                          </CustomTableCell>
                        </>
                      ) : (
                        <>
                          {/* Exam Name and ID */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', fontFamily: 'Poppins' }}>
                                {stripHtml(exam.examTitle)}
                              </Typography>
                            </Box>
                          </CustomTableCell>

                          {/* Class and Subject */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', fontFamily: 'Poppins' }}>
                                {exam.className}
                              </Typography>
                              <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'Poppins', mt: 0.25 }}>
                                {exam.subjectName}
                              </Typography>
                            </Box>
                          </CustomTableCell>

                          {/* Duration Taken */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                            {duration}
                          </CustomTableCell>

                          {/* Completed Date */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '13px', fontFamily: 'Poppins' }}>
                            {gradedDate}
                          </CustomTableCell>

                          {/* Marks */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', color: isExamGrading ? '#64748b' : '#1e293b', fontWeight: isExamGrading ? 500 : 700, fontSize: '13px', fontFamily: 'Poppins' }}>
                            {isExamGrading ? '-' : `${scorePercent}%`}
                          </CustomTableCell>

                          {/* Grade */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                            {isExamGrading ? (
                              <Chip
                                label="Grading"
                                size="small"
                                sx={{
                                  bgcolor: '#FFF7ED',
                                  color: '#D97706',
                                  fontWeight: 700,
                                  fontFamily: 'Poppins',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  height: '22px'
                                }}
                              />
                            ) : getGradeChip(grade)}
                          </CustomTableCell>

                          {/* Type */}
                          <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9' }}>
                            <ExamTypeChip type={exam.examType} />
                          </CustomTableCell>
                        </>
                      )}

                      {/* Action buttons */}
                      <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        {historyStatus !== 'missed' && !isExamGrading && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick();
                            }}
                            sx={{
                              bgcolor: '#F3F4F6',
                              color: '#6B7280',
                              borderRadius: '8px',
                              width: 32,
                              height: 32,
                              '&:hover': { bgcolor: '#E5E7EB' }
                            }}
                          >
                            <VisibilityRoundedIcon sx={{ fontSize: '16px' }} />
                          </IconButton>
                        )}
                      </CustomTableCell>
                    </CustomTableRow>
                  );
                })
              )}
            </CustomTableBody>
          </CustomTable>
        </CustomTableContainer>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <CustomPagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        )}
      </Paper>
    </Box>
  );
}
